import React, { useEffect, useState } from 'react';
import { useHunterFarmerList } from '@/api/auth/useHunterFarmerList';
import { useIsMobile } from '@/hooks';
import { Button } from '@/ui/button';
import { Filter as FilterIcon } from 'lucide-react';

const Filters = ({ onFilterChange }) => {
  const isMobile = useIsMobile();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [selectedHunter, setSelectedHunter] = useState([]);

  const { activeHunterListAPI } = useHunterFarmerList();

  const productsOptions = [
    { id: 1, label: 'Remittance' },
    { id: 2, label: 'Insurance' },
    { id: 3, label: 'Re-KYC' },
    { id: 4, label: 'Referral' },
    { id: 5, label: 'Financing' },
  ];

  const pipelineStage = [
    { id: 1, label: 'Raw' },
    { id: 2, label: 'Fresh' },
    { id: 3, label: 'Profile Completed' },
    { id: 4, label: 'Meeting Completed' },
    // { id: 5, label: 'Onboarded' },
    // { id: 6, label: 'Activated' }
  ];

  const leadStatus = [
    { id: 0, label: 'Active' },
    { id: 1, label: 'Ongoing' },
    { id: 2, label: 'Inactive' },
    { id: 3, label: 'Tanked' },
  ];

  const leadSource = [
    { id: 1, label: 'Self-Generated' },
    { id: 2, label: 'Referral (Hunter)' },
    { id: 3, label: 'Referral (MM Employee)' },
    { id: 4, label: 'Referral (Public)' },
    { id: 5, label: 'Inbound' },
    { id: 6, label: 'Boost' },
    { id: 7, label: 'Referral (Farmer)' },
    { id: 8, label: 'Webscraping' },
    { id: 9, label: 'Lazada' },
    { id: 10, label: 'Bytedance' },
    { id: 11, label: 'Referral Scheme' },
  ];

  const interestLevel = [
    { id: 1, label: 'Cold' },
    { id: 2, label: 'Warm' },
    { id: 3, label: 'Hot' },
  ];

  const region = [
    { id: 1, label: 'MY' },
    { id: 2, label: 'SG' },
    { id: 3, label: 'BN' },
    { id: 4, label: 'AU' },
  ];

  const handleToggleProduct = (optionId) => {
    setSelectedProduct((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleTogglePipeline = (optionId) => {
    setSelectedPipeline((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleToggleStatus = (optionId) => {
    setSelectedStatus((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleToggleSource = (optionId) => {
    setSelectedSource((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleToggleLevel = (optionId) => {
    setSelectedLevel((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleToggleRegion = (optionId) => {
    setSelectedRegion((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  const handleToggleHunterList = (optionId) => {
    setSelectedHunter((prev) =>
      prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId],
    );
  };

  // Reusable Dropdown with optional search + scroll
  const Dropdown = ({
    label,
    selected,
    options = [],
    handleToggle,
    onClear,
    searchable = false,
  }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = searchable
      ? options.filter((option) => {
          const text = (option.label || option.username || '').toLowerCase();
          return text.includes(searchTerm.toLowerCase());
        })
      : options;

    return (
      <div className="relative group">
        <button
          className="px-4 py-2 bg-transparent hover:bg-yellow-100 rounded-lg
                     flex items-center space-x-2 font-medium text-gray-700
                     transition-colors duration-200"
        >
          <span>{label}</span>
          {selected.length > 0 && (
            <span
              className="ml-2 inline-flex items-center justify-center px-2 py-0.5 
                          bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full"
            >
              {selected.length}
            </span>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m19.5 8.25-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>

        <div
          className="absolute left-0 mt-2 w-48 bg-white/80 backdrop-blur-sm border border-gray-200 
                     rounded-lg shadow-lg py-2 z-50 invisible group-hover:visible
                     transition-all duration-200 opacity-0 group-hover:opacity-100
                     max-h-80 overflow-y-auto"
        >
          {/* Search bar for searchable dropdowns */}
          {searchable && (
            <div className="px-3 pb-2">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 border rounded-md text-sm
                           focus:outline-none focus:ring-1 focus:ring-yellow-400"
              />
            </div>
          )}

          {filteredOptions.map((option) => {
            // support both {id} and {user_id}
            const optionId = option.id ?? option.user_id;
            return (
              <label
                key={optionId}
                className="flex items-center px-4 py-2 hover:bg-yellow-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(optionId)}
                  onChange={() => handleToggle(optionId)}
                  className="h-4 w-4 rounded border-gray-300 text-yellow-500 
                             focus:ring-yellow-500 cursor-pointer"
                />
                <span className="ml-3 text-gray-700">
                  {option.label || option.username}
                </span>
              </label>
            );
          })}

          {selected.length > 0 && (
            <div className="border-t border-gray-200 mt-2 pt-2 px-4">
              <button
                onClick={onClear}
                className="text-sm text-yellow-600 hover:text-yellow-700"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const selectedFilters = {
      products: selectedProduct,
      pipelineStage: selectedPipeline,
      leadStatus: selectedStatus,
      leadSource: selectedSource,
      interestLevel: selectedLevel,
      region: selectedRegion,
      hunterId: selectedHunter,
    };
    onFilterChange(selectedFilters);
  }, [
    selectedProduct,
    selectedPipeline,
    selectedStatus,
    selectedSource,
    selectedLevel,
    selectedRegion,
    selectedHunter,
  ]);

  // Count total active filters
  const activeFilterCount = selectedProduct.length + selectedPipeline.length + 
                            selectedStatus.length + selectedSource.length + 
                            selectedLevel.length + selectedRegion.length +
                            selectedHunter.length;

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedProduct([]);
    setSelectedPipeline([]);
    setSelectedStatus([]);
    setSelectedSource([]);
    setSelectedLevel([]);
    setSelectedRegion([]);
    setSelectedHunter([]);
  };

  // Mobile Filter Drawer
  if (isMobile) {
    return (
      <>
        {/* Mobile Filter Button */}
        <Button
          onClick={() => setIsFilterOpen(true)}
          className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300"
        >
          <FilterIcon className="w-4 h-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 
                            bg-yellow-400 text-black text-xs font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </Button>

        {/* Mobile Filter Drawer */}
        {isFilterOpen && (
          <div className="fixed inset-0 z-[100] bg-white flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-bold">Filters</h2>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Filter Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
              {/* Product Filter */}
              <div>
                <h3 className="font-bold text-base mb-3">Product</h3>
                <div className="space-y-2">
                  {productsOptions.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedProduct.includes(option.id)}
                        onChange={() => handleToggleProduct(option.id)}
                        className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pipeline Stage Filter */}
              <div>
                <h3 className="font-bold text-base mb-3">Pipeline Stage</h3>
                <div className="space-y-2">
                  {pipelineStage.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPipeline.includes(option.id)}
                        onChange={() => handleTogglePipeline(option.id)}
                        className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Source Filter */}
              <div>
                <h3 className="font-bold text-base mb-3">Source</h3>
                <div className="space-y-2">
                  {leadSource.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSource.includes(option.id)}
                        onChange={() => handleToggleSource(option.id)}
                        className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Interest Level Filter */}
              <div>
                <h3 className="font-bold text-base mb-3">Interest Level</h3>
                <div className="space-y-2">
                  {interestLevel.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLevel.includes(option.id)}
                        onChange={() => handleToggleLevel(option.id)}
                        className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Region Filter */}
              <div>
                <h3 className="font-bold text-base mb-3">Region</h3>
                <div className="space-y-2">
                  {region.map((option) => (
                    <label key={option.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedRegion.includes(option.id)}
                        onChange={() => handleToggleRegion(option.id)}
                        className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="ml-3 text-gray-900">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Owner Filter */}
              <div>
                <h3 className="font-bold text-base mb-3">Owner</h3>
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Search owner..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(activeHunterListAPI || []).map((option) => {
                    const optionId = option.id ?? option.user_id;
                    return (
                      <label key={optionId} className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedHunter.includes(optionId)}
                          onChange={() => handleToggleHunterList(optionId)}
                          className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                        />
                        <span className="ml-3 text-gray-900">{option.label || option.username}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-2">
              <Button
                onClick={clearAllFilters}
                variant="outline"
                className="flex-1"
              >
                Clear All
              </Button>
              <Button
                onClick={() => setIsFilterOpen(false)}
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop Filter Layout
  return (
    <div>
      <h1 className="font-bold pb-4">Quick Filter</h1>
      <div className="flex flex-row justify-between items-center">
        <p className="font-bold text-gray-500">Filter by:</p>

        <Dropdown
          label="Product"
          selected={selectedProduct}
          options={productsOptions}
          handleToggle={handleToggleProduct}
          onClear={() => setSelectedProduct([])}
        />

        <Dropdown
          label="Pipeline Stage"
          selected={selectedPipeline}
          options={pipelineStage}
          handleToggle={handleTogglePipeline}
          onClear={() => setSelectedPipeline([])}
        />

        {/* <Dropdown
          label="Lead Status"
          selected={selectedStatus}
          options={leadStatus}
          handleToggle={handleToggleStatus}
          onClear={() => setSelectedStatus([])}
        /> */}

        <Dropdown
          label="Source"
          selected={selectedSource}
          options={leadSource}
          handleToggle={handleToggleSource}
          onClear={() => setSelectedSource([])}
        />

        <Dropdown
          label="Interest Level"
          selected={selectedLevel}
          options={interestLevel}
          handleToggle={handleToggleLevel}
          onClear={() => setSelectedLevel([])}
        />

        <Dropdown
          label="Region"
          selected={selectedRegion}
          options={region}
          handleToggle={handleToggleRegion}
          onClear={() => setSelectedRegion([])}
        />

        {/* Owner dropdown: scrollable + searchable */}
        <Dropdown
          label="Owner"
          selected={selectedHunter}
          options={activeHunterListAPI || []}
          handleToggle={handleToggleHunterList}
          onClear={() => setSelectedHunter([])}
          searchable
        />

        <button
          onClick={clearAllFilters}
          className="px-4 py-2 bg-transparent hover:bg-yellow-100 rounded-lg
                     flex items-center space-x-2 font-bold text-gray-700
                     underline transition-colors duration-200"
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
};

export default Filters;
